if (!customElements.get('dut-product-mobile-details')) {
  customElements.define('dut-product-mobile-details', class extends HTMLElement {
    connectedCallback() {
      const template = this.querySelector('[data-dut-mobile-description]');
      const descriptionOutput = this.querySelector('[data-dut-mobile-description-output]');
      const featureOutput = this.querySelector('[data-dut-mobile-features]');
      const specificationOutput = this.querySelector('[data-dut-mobile-specifications]');
      if (!template || !descriptionOutput || !featureOutput || !specificationOutput) return;

      // Products with new catalogue copy carry their features and specifications as data.
      let copyFacts = null;
      try {
        const copyData = this.querySelector('[data-dut-copy-facts]');
        copyFacts = copyData ? JSON.parse(copyData.textContent) : null;
      } catch (error) {
        copyFacts = null;
      }

      const content = template.content.cloneNode(true);
      const wrapper = document.createElement('div');
      wrapper.append(content);
      const headings = [...wrapper.querySelectorAll('h2, h3, h4')];
      const detailsHeading = headings.find((heading) => heading.textContent.trim().toLowerCase() === 'the details');
      const detailsList = detailsHeading?.nextElementSibling?.matches('ul, dl') ? detailsHeading.nextElementSibling : null;
      let specifications = [];
      const researchPlaceholder = /\bunconfirmed\b|\bunknown\b|\bunresolved\b|\bconflict(?:ing|s)?\b|\bnot (?:confirmed|verified|supplied|specified|provided)\b/i;

      if (copyFacts) {
        specifications = (copyFacts.specifications || [])
          .filter((row) => row && row.label && row.value)
          .map((row) => [row.label, row.value]);
      } else if (detailsList?.matches('ul')) {
        detailsList.querySelectorAll(':scope > li').forEach((item) => {
          const labelElement = item.querySelector('strong');
          const label = labelElement?.textContent.replace(/:\s*$/, '').trim() || '';
          const value = item.textContent.replace(labelElement?.textContent || '', '').replace(/^:\s*/, '').trim();
          if (label && value) specifications.push([label, value]);
        });
      } else if (detailsList?.matches('dl')) {
        detailsList.querySelectorAll(':scope > div').forEach((row) => {
          const label = row.querySelector('dt')?.textContent.trim() || '';
          const value = row.querySelector('dd')?.textContent.trim() || '';
          if (label && value) specifications.push([label, value]);
        });
      }

      detailsHeading?.remove();
      detailsList?.remove();
      descriptionOutput.append(...wrapper.childNodes);

      // Missing research is not a product feature or specification.
      specifications = specifications.filter(([, value]) => !researchPlaceholder.test(value) && !/^follow (?:the )?(?:enclosed|supplied) (?:care |cleaning )?instructions\.?$/i.test(value));
      const seenFacts = new Set();
      specifications = specifications.filter(([, value]) => {
        const key = value.toLowerCase().replace(/[.]+$/, '').trim();
        if (seenFacts.has(key)) return false;
        seenFacts.add(key);
        return true;
      });
      const functionalFeature = /air pulses|vibration|settings|speeds|patterns|functions|stimulation|movement|suction|attachments|opening|closure|adjustment|faces|surfaces|texture|firmness|decoration|hearts|handle/i;
      const featureRows = copyFacts ? [] : specifications.filter(([label]) => functionalFeature.test(label) && !/length|width|diameter|size|weight/i.test(label)).slice(0, 5);
      if (copyFacts) {
        (copyFacts.features || []).forEach((feature) => {
          const item = document.createElement('li');
          item.textContent = feature;
          featureOutput.append(item);
        });
      } else if (!featureRows.length) {
        const usefulFact = specifications.find(([label]) => /^(?:material|materials|included|contents|product type|type|design|finish)$/i.test(label));
        if (usefulFact) featureRows.push(usefulFact);
      }
      featureRows.forEach(([label, value]) => {
        const item = document.createElement('li');
        item.textContent = `${label}: ${value.replace(/[.]$/, '')}.`;
        featureOutput.append(item);
      });
      // Each fact belongs to one accordion, never both.
      specifications = specifications.filter((row) => !featureRows.includes(row));

      const measurementPattern = /size|length|width|diameter|circumference|insertable|weight|height|depth|capacity|volume/i;
      const feelPattern = /best for|shape|texture|firmness|settings|speeds|patterns|functions|stimulation|movement|vibration|suction|pressure/i;
      const carePattern = /care|clean|water|lubricant|avoid|inspect|do not|start with|seek medical|storage|wash/i;
      const groups = [['Essential Info', []], ['How It Measures', []], ['How It Feels', []], ['Care & Use', []]];
      specifications.forEach((row) => {
        const label = row[0];
        const index = carePattern.test(label) ? 3 : measurementPattern.test(label) ? 1 : feelPattern.test(label) ? 2 : 0;
        groups[index][1].push(row);
      });

      groups.forEach(([heading, rows]) => {
        if (!rows.length) return;
        const group = document.createElement('section');
        const title = document.createElement('h3');
        const list = document.createElement('ul');
        title.textContent = heading;
        rows.forEach(([label, value]) => {
          const item = document.createElement('li');
          item.textContent = `${label}: ${value}`;
          list.append(item);
        });
        group.append(title, list);
        specificationOutput.append(group);
      });

      if (!featureOutput.children.length) {
        featureOutput.closest('details')?.remove();
      }

      if (!specificationOutput.children.length) {
        specificationOutput.closest('details')?.remove();
      }

      this.renderRecentlyViewed();

      this.setAttribute('data-ready', '');
    }

    renderRecentlyViewed() {
      const currentData = this.querySelector('[data-dut-current-product]');
      const section = this.querySelector('[data-dut-recently-viewed]');
      const track = this.querySelector('[data-dut-recently-viewed-track]');
      if (!currentData || !section || !track) return;

      try {
        const current = JSON.parse(currentData.textContent);
        const storageKey = 'dut-recently-viewed-products';
        const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const recent = Array.isArray(stored) ? stored.filter((item) => item?.handle && item.handle !== current.handle).slice(0, 6) : [];

        recent.forEach((item) => {
          const card = document.createElement('article');
          card.className = 'dut-mobile-product-details__recent-card';
          card.innerHTML = `
            <a class="dut-mobile-product-details__recent-image" href="${this.escape(item.url)}">
              ${item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.imageAlt || item.title)}" loading="lazy" width="720" height="778">` : ''}
            </a>
            <div class="dut-mobile-product-details__recent-copy">
              <a class="dut-mobile-product-details__recent-title" href="${this.escape(item.url)}">${this.escape(item.title)}</a>
              <div class="dut-mobile-product-details__recent-bottom">
                <span class="dut-mobile-product-details__recent-price">${this.escape(item.price || '')}${item.compareAtPrice ? ` <s>${this.escape(item.compareAtPrice)}</s>` : ''}</span>
                <button type="button" class="dut-favourite dut-mobile-product-details__recent-heart" data-favourite-id="${this.escape(String(item.id))}" data-title="${this.escape(item.title)}" data-url="${this.escape(item.url)}" data-image="${this.escape(item.image || '')}" aria-label="Save ${this.escape(item.title)} to favourites" aria-pressed="false">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
              ${item.available === false ? '<span class="dut-mobile-product-details__recent-sold-out">Sold out</span>' : ''}
            </div>`;
          track.append(card);
        });

        if (recent.length) section.hidden = false;
        localStorage.setItem(storageKey, JSON.stringify([current, ...stored.filter((item) => item?.handle !== current.handle)].slice(0, 8)));
      } catch (error) {
        console.warn('Recently viewed products could not be loaded.', error);
      }
    }

    escape(value) {
      const span = document.createElement('span');
      span.textContent = value;
      return span.innerHTML;
    }
  });
}
